import { useEffect, useState, type ReactNode } from "react";
import {
  EmptyState,
  EventFlyer,
  EventMeta,
  HostedBy,
  SaleStatusBadge
} from "../components/public-marketplace";
import { createCheckoutSession, getPublicEvent } from "../lib/api";
import { publicErrorMessage } from "../lib/public-errors";
import { formatTicketPrice, isTierPurchasableNow } from "../lib/public-marketplace";
import type { Event, TicketTier } from "../lib/types";
import { MAX_TICKETS_PER_ORDER } from "../lib/checkout-limits";

interface EventDetailPageProps {
  slug: string;
}

export const MARKETING_EMAIL_OPT_IN_LABEL =
  "I agree to receive promotional emails from AnTix and the event organizer. I can unsubscribe at any time.";
export const MARKETING_SMS_OPT_IN_LABEL =
  "I agree to receive recurring promotional text messages from AnTix and the event organizer at the number provided. Consent is not a condition of purchase. Message and data rates may apply. Reply STOP to opt out.";
export const TRANSACTIONAL_CONTACT_NOTE =
  "Your contact information is still used to deliver tickets and important event updates even if you do not opt into marketing.";

export function normalizeCheckoutPhoneInput(value: string): string | null {
  const digits = value.trim().replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export function createInitialCheckoutForm() {
  return {
    buyerName: "",
    buyerEmail: "",
    buyerPhone: "",
    promoCode: "",
    quantity: "1",
    marketingEmailOptIn: false,
    marketingSmsOptIn: false
  };
}

export function normalizePromoCodeInput(value: string): string | undefined {
  const promoCode = value.trim().toUpperCase();
  return promoCode ? promoCode : undefined;
}

export function buildCheckoutPayload(
  eventId: string,
  ticketTierId: string,
  input: ReturnType<typeof createInitialCheckoutForm>
) {
  return {
    eventId,
    ticketTierId,
    quantity: Number(input.quantity),
    buyerName: input.buyerName.trim(),
    buyerEmail: input.buyerEmail.trim().toLowerCase(),
    buyerPhone: normalizeCheckoutPhoneInput(input.buyerPhone) ?? input.buyerPhone.trim(),
    marketingEmailOptIn: input.marketingEmailOptIn,
    marketingSmsOptIn: input.marketingSmsOptIn,
    promoCode: normalizePromoCodeInput(input.promoCode)
  };
}

export function validateCheckoutForm(input: ReturnType<typeof createInitialCheckoutForm>): string | undefined {
  if (!input.buyerName.trim()) return "Name is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.buyerEmail.trim().toLowerCase())) return "Enter a valid email address.";
  if (!normalizeCheckoutPhoneInput(input.buyerPhone)) return "Enter a valid phone number.";
  const quantityError = validateCheckoutQuantity(input.quantity);
  if (quantityError) return quantityError;
  return undefined;
}

export function getCheckoutQuantityLimit(tier: TicketTier): number {
  // Public inventoryLimit is the tier's total cap; remaining inventory is
  // checked atomically by the existing checkout API.
  return Math.min(MAX_TICKETS_PER_ORDER, tier.perOrderLimit ?? MAX_TICKETS_PER_ORDER, tier.inventoryLimit ?? MAX_TICKETS_PER_ORDER);
}

export function validateCheckoutQuantity(value: string, max = MAX_TICKETS_PER_ORDER): string | undefined {
  const quantity = Number(value);
  if (!value.trim() || !Number.isSafeInteger(quantity) || quantity < 1) {
    return "Quantity must be a whole number of at least 1.";
  }
  if (quantity > max) return `Quantity cannot exceed ${max} for this tier.`;
  return undefined;
}

export function normalizeCheckoutQuantity(value: string, max: number): string {
  const quantity = Number(value);
  return String(Math.min(max, Number.isSafeInteger(quantity) && quantity >= 1 ? quantity : 1));
}

export function CheckoutQuantityInput({ value, max, error, onChange }: {
  value: string;
  max: number;
  error: string;
  onChange: (value: string) => void;
}) {
  const quantity = Number(value) || 1;
  return <div className="quantity-counter" aria-label="Quantity">
    <span>Quantity</span>
    <div className="quantity-counter__controls">
      <button type="button" aria-label="Decrease quantity" disabled={quantity <= 1} onClick={() => onChange(String(quantity - 1))}>−</button>
      <output aria-label={`Quantity: ${quantity}`}>{quantity}</output>
      <button type="button" aria-label="Increase quantity" disabled={quantity >= max} onClick={() => onChange(String(Math.min(max, quantity + 1)))}>+</button>
    </div>
    {error && <span className="form-error" id="checkout-quantity-error" role="alert">{error}</span>}
  </div>;
}

export function EventDescription({ description }: { description: string }) {
  return <p className="marketplace-event__description">{description}</p>;
}

export function getTierAction(
  event: Pick<Event, "status" | "salesStatus" | "capacity">,
  tier: Pick<TicketTier, "id" | "status" | "salesStatus" | "inventoryLimit" | "saleStartsAt" | "saleEndsAt">,
  selectedTierId: string,
  now = Date.now()
) {
  if (tier.status === "sold_out" || tier.salesStatus === "sold_out") return { label: "Sold Out", disabled: true };
  if (tier.status === "paused" || tier.status === "hidden" || tier.salesStatus === "sales_paused") {
    return { label: "Sales Paused", disabled: true };
  }
  if (tier.saleEndsAt && Date.parse(tier.saleEndsAt) <= now) return { label: "Sold Out", disabled: true };
  if (!isTierPurchasableNow(event, tier, now)) return { label: "Unavailable", disabled: true };
  return { label: selectedTierId === tier.id ? "Selected" : "Get Ticket", disabled: false };
}

export function TicketPurchaseList({ event, selectedTierId, now, isSubmitting, onSelect, children }: {
  event: Event;
  selectedTierId: string;
  now: number;
  isSubmitting: boolean;
  onSelect: (tier: TicketTier) => void;
  children: ReactNode;
}) {
  return <div className="marketplace-tier-list">
    {(event.ticketTiers ?? []).map((tier) => {
      const action = getTierAction(event, tier, selectedTierId, now);
      const selected = tier.id === selectedTierId && !action.disabled;
      return <div className="marketplace-tier-option" key={tier.id} data-tier-id={tier.id}>
        <div className={`tier-purchase-row${selected ? " tier-purchase-row--selected" : ""}`}>
          <div className="tier-purchase-row__copy">
            <strong>{tier.name}</strong>
            <span>{formatTicketPrice(tier.priceCents, tier.currency)}</span>
          </div>
          <button type="button"
            className={`tier-purchase-row__button${action.disabled ? " tier-purchase-row__button-disabled" : ""}`}
            disabled={action.disabled || isSubmitting}
            aria-disabled={action.disabled}
            aria-pressed={selected}
            aria-expanded={selected}
            aria-controls={selected ? "ticket-checkout" : undefined}
            onClick={() => { if (!action.disabled && !isSubmitting) onSelect(tier); }}>
            {selected ? "Selected ✓" : action.label}
          </button>
        </div>
        {selected && children}
      </div>;
    })}
  </div>;
}

export default function EventDetailPage({ slug }: EventDetailPageProps) {
  const [event, setEvent] = useState<Event>();
  const [selectedTierId, setSelectedTierId] = useState("");
  const [checkoutForm, setCheckoutForm] = useState(createInitialCheckoutForm);
  const [submitError, setSubmitError] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [availabilityNow, setAvailabilityNow] = useState(Date.now());

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setSubmitError("");
    getPublicEvent(slug)
      .then((item) => {
        setEvent(item);
        setSelectedTierId("");
      })
      .catch(() => {
        setEvent(undefined);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [slug]);

  useEffect(() => {
    const nextCutoff = event?.ticketTiers
      ?.map((tier) => (tier.saleEndsAt ? Date.parse(tier.saleEndsAt) : Number.NaN))
      .filter((cutoff) => !Number.isNaN(cutoff) && cutoff > Date.now())
      .sort((left, right) => left - right)[0];
    if (!nextCutoff) return;
    const timer = window.setTimeout(() => setAvailabilityNow(Date.now()), Math.max(nextCutoff - Date.now() + 10, 10));
    return () => window.clearTimeout(timer);
  }, [event, availabilityNow]);

  if (isLoading) {
    return (
      <section className="page marketplace-page marketplace-page-detail">
        <div className="marketplace-event marketplace-event-detail marketplace-event-skeleton">
          <div className="marketplace-flyer marketplace-flyer-skeleton" />
          <div className="marketplace-event__body">
            <div className="marketplace-skeleton-line marketplace-skeleton-line-short" />
            <div className="marketplace-skeleton-line marketplace-skeleton-line-title" />
            <div className="marketplace-skeleton-line" />
            <div className="marketplace-skeleton-line marketplace-skeleton-line-wide" />
          </div>
        </div>
      </section>
    );
  }

  if (hasError) {
    return (
      <section className="page marketplace-page">
        <EmptyState
          title="We couldn't load this event right now"
          body="Please try again in a moment."
          action={
            <a className="marketplace-link-button" href="/tickets">
              Back to Tickets
            </a>
          }
        />
      </section>
    );
  }

  if (!event) {
    return (
      <section className="page marketplace-page">
        <EmptyState
          title="Event not found"
          body="This event may no longer be available."
          action={
            <a className="marketplace-link-button" href="/tickets">
              Back to Tickets
            </a>
          }
        />
      </section>
    );
  }

  const selectedTier = event.ticketTiers?.find((tier) => tier.id === selectedTierId);

  async function beginCheckout() {
    if (!event || !selectedTier || !isTierPurchasableNow(event, selectedTier, availabilityNow)) {
      setSubmitError("Select a ticket tier first.");
      return;
    }

    const nextQuantityError = validateCheckoutQuantity(checkoutForm.quantity, getCheckoutQuantityLimit(selectedTier));
    setQuantityError(nextQuantityError ?? "");
    if (nextQuantityError) return;

    const validationError = validateCheckoutForm(checkoutForm);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    try {
      const session = await createCheckoutSession(buildCheckoutPayload(event.id, selectedTier.id, checkoutForm));
      window.location.assign(session.checkoutUrl);
    } catch (error) {
      setSubmitError(publicErrorMessage(error, "We couldn't start checkout. Please try again."));
      setIsSubmitting(false);
    }
  }

  return (
    <section className="page marketplace-page marketplace-page-detail">
      <div className="marketplace-back-row">
        <a className="marketplace-back-link marketplace-back-link-compact" href="/tickets">
          ← Back to Tickets
        </a>
      </div>

      <article className="marketplace-event marketplace-event-detail">
        <EventFlyer event={event} priority />
        <div className="marketplace-event__body">
          <div className="marketplace-event__topline">
            <SaleStatusBadge event={event} />
            {event.lowestAvailablePriceCents !== undefined && (
              <span className="marketplace-price-kicker">From {formatTicketPrice(event.lowestAvailablePriceCents)}</span>
            )}
          </div>
          <h1>{event.title}</h1>
          <HostedBy hostedBy={event.hostedBy} />
          <EventMeta event={event} />
          {event.description && <EventDescription description={event.description} />}
          {(event.dressCode || event.venueAddress) && (
            <div className="marketplace-notes">
              {event.dressCode && (
                <p>
                  <span>Dress code</span>
                  {event.dressCode}
                </p>
              )}
              {event.venueAddress && (
                <p>
                  <span>Address</span>
                  {event.venueAddress}
                </p>
              )}
            </div>
          )}
          <TicketPurchaseList event={event} selectedTierId={selectedTierId} now={availabilityNow}
            isSubmitting={isSubmitting} onSelect={(tier) => {
              setSelectedTierId(tier.id);
              setQuantityError("");
              setCheckoutForm((current) => ({ ...current, quantity: normalizeCheckoutQuantity(current.quantity, getCheckoutQuantityLimit(tier)) }));
              setSubmitError("");
            }}>
            {selectedTier && isTierPurchasableNow(event, selectedTier, availabilityNow) && (
              <div className="marketplace-checkout-card" id="ticket-checkout">
                <div className="marketplace-checkout-card__header">
                  <strong>{selectedTier.priceCents === 0 ? "Free checkout" : "Checkout"}</strong>
                  <span>{selectedTier.name} - {formatTicketPrice(selectedTier.priceCents, selectedTier.currency)}</span>
                </div>
                <div className="form-grid compact-form">
                  <label>
                    Full Name
                    <input
                      value={checkoutForm.buyerName}
                      onChange={(nextEvent) => setCheckoutForm((current) => ({ ...current, buyerName: nextEvent.target.value }))}
                      autoComplete="name"
                      required
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={checkoutForm.buyerEmail}
                      onChange={(nextEvent) => setCheckoutForm((current) => ({ ...current, buyerEmail: nextEvent.target.value }))}
                      autoComplete="email"
                      required
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      type="tel"
                      value={checkoutForm.buyerPhone}
                      onChange={(nextEvent) => setCheckoutForm((current) => ({ ...current, buyerPhone: nextEvent.target.value }))}
                      autoComplete="tel-national"
                      inputMode="tel"
                      placeholder="(352) 555-1234"
                      required
                    />
                  </label>
                  <label>
                    Promo code
                    <input
                      value={checkoutForm.promoCode}
                      onChange={(nextEvent) => setCheckoutForm((current) => ({ ...current, promoCode: nextEvent.target.value }))}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="Optional"
                    />
                  </label>
                  <CheckoutQuantityInput value={checkoutForm.quantity}
                    max={getCheckoutQuantityLimit(selectedTier)} error={quantityError}
                    onChange={(quantity) => {
                      setCheckoutForm((current) => ({ ...current, quantity }));
                      setQuantityError("");
                    }} />
                </div>
                <div className="stack-list compact-form">
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={checkoutForm.marketingEmailOptIn}
                      onChange={(nextEvent) =>
                        setCheckoutForm((current) => ({ ...current, marketingEmailOptIn: nextEvent.target.checked }))
                      }
                    />
                    <span>{MARKETING_EMAIL_OPT_IN_LABEL}</span>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={checkoutForm.marketingSmsOptIn}
                      onChange={(nextEvent) =>
                        setCheckoutForm((current) => ({ ...current, marketingSmsOptIn: nextEvent.target.checked }))
                      }
                    />
                    <span>{MARKETING_SMS_OPT_IN_LABEL}</span>
                  </label>
                  <p className="panel-copy">{TRANSACTIONAL_CONTACT_NOTE}</p>
                </div>
                {submitError && <p className="form-error">{submitError}</p>}
                <div className="button-row">
                  <button className="primary-action" type="button" disabled={isSubmitting} onClick={() => void beginCheckout()}>
                    {selectedTier.priceCents === 0 ? "Get Ticket" : "Continue to Checkout"}
                  </button>
                </div>
              </div>
            )}
          </TicketPurchaseList>
        </div>
      </article>
    </section>
  );
}
